/**
 * Grid cell type constants and helper utilities
 */

export const CellType = {
  EMPTY: 'empty',
  WALKWAY: 'walkway',
  SHELF: 'shelf',
  WALL: 'wall',
  ENTRANCE: 'entrance',
  CHECKOUT: 'checkout',
}

/** Labels shown in the UI for each cell type */
export const CellTypeLabel = {
  [CellType.EMPTY]: 'Leeg',
  [CellType.WALKWAY]: 'Looppad',
  [CellType.SHELF]: 'Schap',
  [CellType.WALL]: 'Muur',
  [CellType.ENTRANCE]: 'Ingang',
  [CellType.CHECKOUT]: 'Kassa',
}

/** Emoji icons for the cell type toolbar */
export const CellTypeIcon = {
  [CellType.EMPTY]: '⬜',
  [CellType.WALKWAY]: '🚶',
  [CellType.SHELF]: '🏷️',
  [CellType.WALL]: '🧱',
  [CellType.ENTRANCE]: '🚪',
  [CellType.CHECKOUT]: '💳',
}

/** Colors used to render each cell type on the grid */
export const CellTypeColor = {
  [CellType.EMPTY]: 'transparent',
  [CellType.WALKWAY]: 'rgba(148, 163, 184, 0.08)',
  [CellType.SHELF]: 'rgba(148, 163, 184, 0.25)',
  [CellType.WALL]: 'rgba(30, 41, 59, 0.9)',
  [CellType.ENTRANCE]: 'rgba(52, 211, 153, 0.4)',
  [CellType.CHECKOUT]: 'rgba(251, 191, 36, 0.4)',
}

/** Default product categories with assigned colors */
export const DEFAULT_CATEGORIES = [
  { id: 'zuivel', name: 'Zuivel', color: '#60A5FA' },
  { id: 'brood', name: 'Brood & Gebak', color: '#F59E0B' },
  { id: 'groente', name: 'Groente & Fruit', color: '#34D399' },
  { id: 'vlees', name: 'Vlees & Vis', color: '#EF4444' },
  { id: 'dranken', name: 'Dranken', color: '#8B5CF6' },
  { id: 'diepvries', name: 'Diepvries', color: '#06B6D4' },
  { id: 'schoonmaak', name: 'Schoonmaak', color: '#EC4899' },
  { id: 'huishoudelijk', name: 'Huishoudelijk', color: '#F97316' },
  { id: 'snacks', name: 'Snacks & Snoep', color: '#A78BFA' },
  { id: 'conserven', name: 'Conserven & Pasta', color: '#14B8A6' },
  { id: 'ontbijt', name: 'Ontbijt & Beleg', color: '#FBBF24' },
  { id: 'overig', name: 'Overig', color: '#94A3B8' },
]

/**
 * Creates an empty grid of the given dimensions.
 * Each cell defaults to CellType.EMPTY.
 */
export function createEmptyGrid(width, height) {
  return Array.from({ length: height }, () =>
    Array.from({ length: width }, () => CellType.EMPTY)
  )
}

/**
 * Deep-clones a grid (2D array of strings).
 */
export function cloneGrid(grid) {
  return grid.map(row => [...row])
}

/**
 * Checks whether a cell at (x, y) is walkable.
 * Walkable = EMPTY, WALKWAY, ENTRANCE, or CHECKOUT.
 */
export function isWalkable(cellType) {
  return (
    cellType === CellType.EMPTY ||
    cellType === CellType.WALKWAY ||
    cellType === CellType.ENTRANCE ||
    cellType === CellType.CHECKOUT
  )
}

/**
 * Finds the centroid of a set of cells.
 * @param {Array<[number, number]>} cells — Array of [x, y] coordinates
 * @returns {{ x: number, y: number }}
 */
export function getCentroid(cells) {
  if (!cells.length) return { x: 0, y: 0 }
  const sumX = cells.reduce((s, [x]) => s + x, 0)
  const sumY = cells.reduce((s, [, y]) => s + y, 0)
  return {
    x: Math.round(sumX / cells.length),
    y: Math.round(sumY / cells.length),
  }
}

/**
 * Finds the nearest walkable cell to a given point on the grid.
 * Searches in expanding rings (Manhattan distance).
 */
export function findNearestWalkable(grid, targetX, targetY) {
  const height = grid.length
  const width = grid[0]?.length || 0

  // If target itself is walkable, return it
  if (
    targetY >= 0 && targetY < height &&
    targetX >= 0 && targetX < width &&
    isWalkable(grid[targetY][targetX])
  ) {
    return { x: targetX, y: targetY }
  }

  // BFS outward
  const maxDist = Math.max(width, height)
  for (let d = 1; d <= maxDist; d++) {
    for (let dy = -d; dy <= d; dy++) {
      for (let dx = -d; dx <= d; dx++) {
        if (Math.abs(dx) + Math.abs(dy) !== d) continue
        const nx = targetX + dx
        const ny = targetY + dy
        if (ny >= 0 && ny < height && nx >= 0 && nx < width && isWalkable(grid[ny][nx])) {
          return { x: nx, y: ny }
        }
      }
    }
  }

  return { x: targetX, y: targetY }
}

/**
 * Manhattan distance between two points.
 */
export function manhattan(ax, ay, bx, by) {
  return Math.abs(ax - bx) + Math.abs(ay - by)
}

/**
 * Generates a unique ID (short random string).
 */
export function generateId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8)
}

/**
 * Finds all cells of a given type in the grid.
 * @returns {Array<[number, number]>} — Array of [x, y] coordinates
 */
export function findCellsOfType(grid, cellType) {
  const cells = []
  for (let y = 0; y < grid.length; y++) {
    for (let x = 0; x < grid[y].length; x++) {
      if (grid[y][x] === cellType) cells.push([x, y])
    }
  }
  return cells
}
